import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Zap } from "lucide-react";

import StatusPill from "@/components/StatusPill";
import MockBadge from "@/components/MockBadge";
import { admin } from "@/lib/api";
import { fmtTime, shortId } from "@/lib/format";

export default function TripRequests() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [running, setRunning] = useState(null);

  // Live polling (react-query, 8s).
  const { data: rows = [] } = useQuery({
    queryKey: ["trips"],
    queryFn: () => admin.trips({ limit: 200 }),
    refetchInterval: 8000,
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["trips"] });

  const runQuote = async (id) => {
    setRunning(id);
    try {
      const r = await admin.runQuote(id);
      if (r.ok) toast.success(`Quote generated in ${r.latency_ms}ms`);
      else toast.error(`Pipeline failed: ${r.reason || "unknown"}`);
      await refresh();
    } finally {
      setRunning(null);
    }
  };

  return (
    <div data-testid="page-trips" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Radar · Demand intake</div>
          <h1 className="font-display text-[40px] leading-[1.02]">Trip Requests</h1>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <MockBadge />
          <button
            type="button"
            data-testid="btn-create-trip"
            onClick={() => setShowNew(true)}
            style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
            className="mono inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.12em] hover:opacity-90"
          >
            <Plus size={14} /> New Trip Request
          </button>
        </div>
      </div>

      {showNew && <NewTripForm onClose={() => setShowNew(false)} onCreated={refresh} />}

      <div className="pj-card overflow-x-auto">
        <table className="w-full min-w-[820px] mono text-[12px]">
          <thead style={{ color: "rgb(var(--dim))" }}>
            <tr className="text-left">
              <th className="px-4 py-3 font-normal">ID</th>
              <th className="px-4 py-3 font-normal">Route</th>
              <th className="px-4 py-3 font-normal">Pax</th>
              <th className="px-4 py-3 font-normal">Cabin</th>
              <th className="px-4 py-3 font-normal">Channel</th>
              <th className="px-4 py-3 font-normal">Status</th>
              <th className="px-4 py-3 font-normal">Created</th>
              <th className="px-4 py-3 font-normal text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr
                key={t.id}
                data-testid={`trip-${t.id}`}
                style={{ borderTop: "1px solid rgb(var(--ln))" }}
              >
                <td className="px-4 py-3 text-[rgb(var(--dim))] whitespace-nowrap">{shortId(t.id)}</td>
                <td className="px-4 py-3 max-w-[280px] truncate" title={(t.legs || []).map((l) => `${l.from_airport} → ${l.to_airport}`).join(" · ")}>
                  {(t.legs || []).map((l) => `${l.from_airport} → ${l.to_airport}`).join(" · ")}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{t.pax}</td>
                <td className="px-4 py-3 capitalize whitespace-nowrap">{(t.preferred_cabin || "").replace("_", " ")}</td>
                <td className="px-4 py-3 text-[rgb(var(--dim))] whitespace-nowrap">{t.source_channel}</td>
                <td className="px-4 py-3"><StatusPill state={t.status} /></td>
                <td className="px-4 py-3 text-[rgb(var(--dim))] whitespace-nowrap">{fmtTime(t.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => runQuote(t.id)}
                    disabled={running === t.id || t.status !== "quote_requested"}
                    data-testid={`btn-run-quote-${t.id}`}
                    className="mono px-2 py-1 text-[10px] uppercase tracking-[0.14em] border border-[rgb(var(--border-strong))] hover:border-[rgb(169,130,28)] text-[rgb(var(--fg))] hover:text-[rgb(169,130,28)] disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <Zap size={11} /> {running === t.id ? "Running…" : "Run Quote"}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 mono text-[11px] text-[rgb(var(--dim))]">No trip requests yet. Create one or open the widget.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewTripForm({ onClose, onCreated }) {
  const tomorrow = new Date(Date.now() + 86400000 * 2).toISOString().slice(0, 16);
  const [from_airport, setFrom] = useState("KJFK");
  const [to_airport, setTo] = useState("KLAS");
  const [departure_at, setDep] = useState(tomorrow);
  const [pax, setPax] = useState(4);
  const [cabin, setCabin] = useState("midsize");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await admin.createTrip({
        pax: Number(pax),
        preferred_cabin: cabin,
        legs: [{ from_airport, to_airport, departure_at: new Date(departure_at).toISOString() }],
        full_name: name || null,
        email: email || null,
      });
      toast.success("Trip request created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="new-trip-form"
      className="pj-card p-5 mb-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 items-end"
    >
      <Field label="From" value={from_airport} onChange={setFrom} testid="new-trip-from" />
      <Field label="To" value={to_airport} onChange={setTo} testid="new-trip-to" />
      <Field label="Departure" value={departure_at} onChange={setDep} type="datetime-local" testid="new-trip-dep" />
      <Field label="Pax" value={pax} onChange={setPax} type="number" testid="new-trip-pax" />
      <label className="block min-w-0">
        <span className="pj-label block mb-1.5">Cabin</span>
        <select
          value={cabin}
          onChange={(e) => setCabin(e.target.value)}
          data-testid="new-trip-cabin"
          className="w-full mono text-[12px] px-2 py-2"
          style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
        >
          <option value="light">Light</option>
          <option value="midsize">Midsize</option>
          <option value="super_mid">Super-mid</option>
          <option value="heavy">Heavy</option>
          <option value="ultra_long">Ultra-long</option>
        </select>
      </label>
      <Field label="Client Name" value={name} onChange={setName} testid="new-trip-name" />
      <Field label="Client Email" value={email} onChange={setEmail} testid="new-trip-email" />
      <div className="col-span-2 sm:col-span-3 lg:col-span-7 flex flex-wrap justify-end gap-2 mt-2">
        <button type="button" onClick={onClose} className="mono px-3 py-2 text-[11px] uppercase tracking-[0.14em] border border-[rgb(var(--border-strong))] text-[rgb(var(--fg))] hover:border-[rgb(169,130,28)] hover:text-[rgb(169,130,28)]">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="new-trip-submit" style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }} className="mono px-3 py-2 text-[11px] uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-40">
          {busy ? "Creating…" : "Create Trip Request"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block min-w-0">
      <span className="pj-label block mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full min-w-0 mono text-[12px] px-2 py-2"
        style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
      />
    </label>
  );
}
